import React, { useEffect, useState, useRef, useCallback } from 'react';
import ReactAvatarEditor from 'react-avatar-editor';
import PropTypes from 'prop-types';
import { Flex, Box, Button, Image } from 'rebass';
import { useWindowSize } from 'react-use';
import {
    calculateAspectRatioFit,
    ImageFromFileOrSrc,
    getAspectRatio,
    scalePosition,
    renderPositionForVGBImage,
    isFileOrBlob,
    imageUrlToBlob,
} from './helper';
import BufferingOverlay from 'app/modal/BufferingOverlay';
import Grid from 'app/grid/index';
import ZoomSlider from 'app/form/formik/cover/ZoomSlider';
import { UAParser } from 'ua-parser-js';
import * as html2canvas from 'html2canvas';
import { getPillar } from 'stories/helper';
import { capCanvasDims } from 'collections/book-creation/helper';


function Editor({ image, onClear, onAccept, setSubmitting, posterAttributes = null, mediaAspectRatio, imageWidthInEditor, imageHeightInEditor, frameSrc = null }) {
    const editorRef = useRef();
    const ref = useRef();
    const [zoom, setZoom] = useState(posterAttributes ? scalePosition(posterAttributes?.scale) : 1.2);
    const [position, setPositions] = useState(posterAttributes ? posterAttributes.position : null);
    const [imageInfo, setImageInfo] = useState(null);
    const [newImageObj, setNewImageObj] = useState(null);
    const [boxWidth, setBoxWidth] = useState(null);
    const [frameDimensions, setFrameDimensions] = useState(null);
    const [imageDims, setImageDims] = useState(null);
    const { width } = useWindowSize();

    const parser = new UAParser();
    const browserName = parser.getResult().browser.name;
    const isChromeBrowser = browserName === 'Chrome';

    useEffect(() => {
        if (!frameSrc) return;
        const img = new window.Image();
        img.onload = () => setFrameDimensions({ width: img.naturalWidth, height: img.naturalHeight });
        img.src = frameSrc;
    }, [frameSrc]);
    const aspectSrcWidth = frameDimensions?.width || posterAttributes?.aspectRatio?.width || 300;
    const aspectSrcHeight = frameDimensions?.height || posterAttributes?.aspectRatio?.height || 346;
    const { width: AvatarEditorWidth, height: AvatarEditorHeight } = calculateAspectRatioFit(
        aspectSrcWidth,
        aspectSrcHeight,
        300,
        346
    );

    const isFromBookCreation = window?.location?.href.includes('/book-creation?step=4')
    const handleChange = async (url) => {
        try {
            const blob = await imageUrlToBlob(url);
            setNewImageObj(URL.createObjectURL(blob));
        } catch (err) {
            setNewImageObj(url);
        }
    };

    useEffect(() => {
        if (image && !isFileOrBlob(image) && image.indexOf(window.location.origin) === -1) {
            handleChange(image);
        } else setNewImageObj(image);
    }, [image]);

    // Read the natural dimensions of the image being cropped. On a NEW upload the clip
    // media doesn't exist yet, so mediaAspectRatio is empty and the editor would size its
    // canvas from the modal box (imageWidthInEditor/Height ≈ 261×321) — a different aspect
    // ratio than the preview, which renders at the image's real dimensions (e.g. 841×1121).
    // That mismatch is what made freshly-uploaded photos look mis-cropped until re-cropped.
    useEffect(() => {
        if (!newImageObj) return undefined;
        const isBlobLike = typeof newImageObj !== 'string';
        const src = isBlobLike ? URL.createObjectURL(newImageObj) : newImageObj;
        const img = new window.Image();
        img.onload = () => setImageDims({ width: img.naturalWidth, height: img.naturalHeight });
        img.src = src;
        return () => { if (isBlobLike) URL.revokeObjectURL(src); };
    }, [newImageObj]);

    // Canvas aspect ratio for the cropper. Prefer the clip's stored dimensions (edit
    // case); fall back to the uploaded image's OWN natural dimensions (new upload). Both
    // match what the preview/thumbnail render at, so the crop stays consistent. Capped
    // (AR-preserving) to stay under the mobile-Safari canvas-area limit.
    const aspectForCanvas = (mediaAspectRatio && mediaAspectRatio.width && mediaAspectRatio.height)
        ? mediaAspectRatio
        : imageDims;
    const cappedAspect = capCanvasDims(aspectForCanvas?.width, aspectForCanvas?.height);

    const handleDone = useCallback(() => {
        const perform = async () => {
            if (setSubmitting)
                setSubmitting(true)
            try {
                let blob;
                if (window.location.search.includes('?step=1')) {
                    const capturedCanvas = await html2canvas(ref?.current, {
                        useCORS: true,
                        backgroundColor: null
                    });
                    blob = await new Promise((resolve) => capturedCanvas.toBlob(resolve, 'image/png'));
                } else {
                    const editorCanvas = editorRef.current.getImageScaledToCanvas();

                    // if (frameSrc) {
                    //     const compositeCanvas = document.createElement('canvas');
                    //     compositeCanvas.width = editorCanvas.width;
                    //     compositeCanvas.height = editorCanvas.height;
                    //     const ctx = compositeCanvas.getContext('2d');
                    //     ctx.drawImage(editorCanvas, 0, 0);

                    //     // ✅ Convert frame image to blob first to handle CORS
                    //     try {
                    //         const frameBlob = await fetch(frameSrc, {
                    //             mode: 'cors',
                    //             credentials: 'omit'
                    //         }).then(res => res.blob());

                    //         const frameUrl = URL.createObjectURL(frameBlob);

                    //         await new Promise((resolve, reject) => {
                    //             const frameImg = new window.Image();
                    //             frameImg.onload = () => {
                    //                 try {
                    //                     ctx.drawImage(frameImg, 0, 0, compositeCanvas.width, compositeCanvas.height);
                    //                     URL.revokeObjectURL(frameUrl);
                    //                     resolve();
                    //                 } catch (err) {
                    //                     reject(err);
                    //                 }
                    //             };
                    //             frameImg.onerror = reject;
                    //             frameImg.src = frameUrl;
                    //         });

                    //         blob = await new Promise((resolve) => compositeCanvas.toBlob(resolve, 'image/png'));
                    //     } catch (corsError) {
                    //         console.warn('⚠️ Frame CORS error, using editor canvas only:', corsError);
                    //         // ✅ Fallback to editor canvas only
                    //         blob = await new Promise((resolve) => editorCanvas.toBlob(resolve, 'image/png'));
                    //     }
                    // } else {
                    blob = await fetch(editorCanvas.toDataURL()).then((res) => res.blob());
                    // }
                }

                if (posterAttributes) {
                    onAccept({
                        croppedPostion: editorRef.current.getCroppingRect(),
                        imageInfo,
                        scale: zoom,
                        position,
                        croppedBlob: blob,
                    });
                } else onAccept(blob);
            } catch (err) {
                console.error('❌ Error in handleDone:', err);
                setSubmitting(false)
            }
        };

        perform();
    }, [zoom, position, imageInfo, onAccept, posterAttributes, frameSrc]);
    let height = AvatarEditorHeight;
    let imageWidth = AvatarEditorWidth;

    if (width < 500) {
        if (!posterAttributes) {
            height = getAspectRatio(AvatarEditorWidth);
            if (boxWidth) imageWidth = boxWidth - 50;
        } else {
            // Scale both dimensions proportionally to preserve the frame's aspect ratio
            const mobileScale = (AvatarEditorWidth - 50) / AvatarEditorWidth;
            imageWidth = AvatarEditorWidth * mobileScale;
            height = AvatarEditorHeight * mobileScale;
        }
    }

    const handleZoom = (e) => {
        if (posterAttributes) {
            const scaleDelta = e.deltaY > 0 ? -1 : 1;
            const newScale = zoom + scaleDelta;
            if (newScale <= 100 && newScale >= 5) setZoom(newScale);
        }
    };

    // ── Mobile only: pinch-to-zoom (drag-to-pan is already handled natively by
    // ReactAvatarEditor's onPositionChange). Desktop keeps the slider + wheel zoom.
    const isTouchDevice =
        typeof window !== 'undefined' &&
        ('ontouchstart' in window || (navigator.maxTouchPoints || 0) > 0);
    const useTouchGestures = width < 500 && isTouchDevice;
    const zoomMin = posterAttributes ? 5 : 1;
    const zoomMax = posterAttributes ? 100 : 3;

    // Keep the latest zoom readable inside the (once-attached) native listeners.
    const zoomRef = useRef(zoom);
    useEffect(() => { zoomRef.current = zoom; }, [zoom]);
    const pinchRef = useRef(null);
    const gestureActiveRef = useRef(false);

    useEffect(() => {
        const el = ref.current;
        if (!el || !useTouchGestures) return undefined;

        const applyZoom = (raw) => {
            const clamped = Math.min(zoomMax, Math.max(zoomMin, raw));
            setZoom(posterAttributes ? Math.round(clamped) : Number(clamped.toFixed(2)));
        };
        const distance = (touches) =>
            Math.hypot(
                touches[0].clientX - touches[1].clientX,
                touches[0].clientY - touches[1].clientY,
            );

        // iOS Safari AND iOS Chrome (both WebKit) implement pinch via non-standard
        // GestureEvents and usually withhold the 2-finger touchmove sequence, so the
        // touch-distance path below never fires there — these handle those browsers.
        const onGestureStart = (e) => {
            e.preventDefault();
            gestureActiveRef.current = true;
            pinchRef.current = { startZoom: Number(zoomRef.current) || zoomMin };
        };
        const onGestureChange = (e) => {
            e.preventDefault();
            if (pinchRef.current) applyZoom(pinchRef.current.startZoom * e.scale);
        };
        const onGestureEnd = (e) => {
            e.preventDefault();
            gestureActiveRef.current = false;
            pinchRef.current = null;
        };

        // Touch-distance pinch for non-WebKit touch browsers (e.g. Android Chrome).
        const onTouchStart = (e) => {
            if (e.touches.length === 2 && !gestureActiveRef.current) {
                pinchRef.current = {
                    startDist: distance(e.touches) || 1,
                    startZoom: Number(zoomRef.current) || zoomMin,
                };
            }
        };
        const onTouchMove = (e) => {
            if (gestureActiveRef.current) return; // WebKit gesture handlers own it
            if (e.touches.length === 2 && pinchRef.current && pinchRef.current.startDist) {
                // We drive zoom from the pinch; stop the browser's native page zoom.
                // (React's onTouchMove is passive, so this must be a native listener.)
                e.preventDefault();
                const ratio = distance(e.touches) / pinchRef.current.startDist;
                applyZoom(pinchRef.current.startZoom * ratio);
            }
        };
        const endPinch = (e) => {
            if (e.touches.length < 2) pinchRef.current = null;
        };

        el.addEventListener('gesturestart', onGestureStart, { passive: false });
        el.addEventListener('gesturechange', onGestureChange, { passive: false });
        el.addEventListener('gestureend', onGestureEnd, { passive: false });
        el.addEventListener('touchstart', onTouchStart, { passive: false });
        el.addEventListener('touchmove', onTouchMove, { passive: false });
        el.addEventListener('touchend', endPinch, { passive: false });
        el.addEventListener('touchcancel', endPinch, { passive: false });
        return () => {
            el.removeEventListener('gesturestart', onGestureStart);
            el.removeEventListener('gesturechange', onGestureChange);
            el.removeEventListener('gestureend', onGestureEnd);
            el.removeEventListener('touchstart', onTouchStart);
            el.removeEventListener('touchmove', onTouchMove);
            el.removeEventListener('touchend', endPinch);
            el.removeEventListener('touchcancel', endPinch);
        };
    }, [useTouchGestures, zoomMin, zoomMax, posterAttributes]);

    const isBuffering = !newImageObj;

    return (
        <Box
            ref={(el) => {
                if (!el) return;
                setBoxWidth(el.getBoundingClientRect().width);
            }}
        >
            {posterAttributes && (
                <ImageFromFileOrSrc
                    sx={{ display: 'none' }}
                    file={image}
                    src={image}
                />
            )}
            <Flex flexDirection="column" maxWidth={width > 480 ? 480 : width}>
                <BufferingOverlay isBuffering={isBuffering} m="auto">
                    <Box sx={{ position: 'relative', bg: 'black', display: 'block', ...(useTouchGestures && { touchAction: 'none' }) }} height={imageHeightInEditor || height} width={imageWidthInEditor || imageWidth} ref={ref}>
                        <ReactAvatarEditor
                            image={newImageObj}
                            // Cap the canvas to the browser area limit (AR-preserving) so the
                            // editor renders on mobile Safari; same clip AR as the preview.
                            height={cappedAspect.height || imageHeightInEditor || height}
                            width={cappedAspect.width || imageWidthInEditor || imageWidth}
                            ref={editorRef}
                            color={[250, 250, 250, 0]} // RGBA
                            scale={posterAttributes ? (+zoom * 3) / 100 : zoom}
                            position={renderPositionForVGBImage(position)}
                            onPositionChange={(pos) => (posterAttributes ? setPositions(pos) : {})}
                            onWheel={handleZoom}
                            crossOrigin="anonymous"
                            style={{
                                minWidth: "100%",
                                width: "100%",
                                height: "100%",
                                objectFit: 'cover',
                            }}
                        />
                        {frameSrc && <Image
                            src={frameSrc}
                            width={(imageWidthInEditor || 0) + (getPillar(imageWidthInEditor || 0) * 2)}
                            maxWidth='inherit'
                            sx={{
                                position: 'absolute',
                                // Match the preview/thumbnail on every device (was 'unset'
                                // on mobile, which mis-positioned the frame overlay).
                                top: -getPillar(imageWidthInEditor || 0),
                                left: -getPillar(imageWidthInEditor || 0),
                                pointerEvents: 'none',
                                objectFit: 'cover',
                                zIndex: 1,
                            }}
                        />}
                    </Box>
                </BufferingOverlay>
                <Grid
                    alignItems="center"
                    gridTemplateColumns={width < 500 ? 'block' : '1fr max-content max-content'}
                    gridGap={[4]}
                    pt={[2, 4]}
                    mt={20}
                    style={{
                        zIndex: 1,
                        background: '#FAFAFA',
                    }}
                >
                    {!useTouchGestures && (
                        <Box>
                            <ZoomSlider
                                id="zoom_control"
                                zoom={zoom}
                                onChange={(e) => setZoom(e.target.value)}
                                max={posterAttributes ? 100 : 3}
                                min={posterAttributes ? 5 : 1}
                                step={posterAttributes ? '1' : '.1'}
                                width="80px"
                                showActualValue={!posterAttributes}
                                showSign={posterAttributes ? '%' : ''}
                                style={{ width: '100%' }}
                            />
                        </Box>
                    )}
                    {width < 500 ? (
                        <Flex justifyContent="center" sx={{ gap: '10px', width: '100%' }}>
                            {!isFromBookCreation && (
                                <Button
                                    width="50%"
                                    p={"10px 20px"}
                                    type="button"
                                    fontFamily="Poppins"
                                    fontSize="16px"
                                    variant="muted"
                                    onClick={onClear}
                                    sx={{
                                        borderRadius: '23px',
                                        border: '1.5px solid #8F8F8F'
                                    }}
                                >
                                    Remove
                                </Button>
                            )}
                            <Button
                                width={'50%'}
                                type="button"
                                bg="transparent"
                                fontFamily="Poppins"
                                fontSize="16px"
                                color={'#9B59B6'}
                                disabled={isBuffering}
                                onClick={handleDone}
                                sx={{
                                    borderRadius: '23px',
                                    border: '1.5px solid #9B59B6'
                                }}
                            >
                                Confirm
                            </Button>
                        </Flex>
                    ) : (
                        <>
                            {!isFromBookCreation && (
                                <Button fontFamily="Poppins"
                                    fontSize="16px"
                                    p={"10px 20px"}
                                    type="button"
                                    variant="muted" onClick={onClear}
                                    sx={{
                                        borderRadius: '23px',
                                        border: '1.5px solid #8F8F8F'
                                    }}>
                                    Remove
                                </Button>
                            )}
                            <Button
                                fontFamily="Poppins"
                                fontSize="16px"
                                type="button"
                                disabled={isBuffering}
                                bg="transparent"
                                p={"10px 20px"}
                                color={'#9B59B6'}
                                sx={{
                                    borderRadius: '23px',
                                    border: '1.5px solid #9B59B6'
                                }}
                                onClick={handleDone}
                            >
                                Confirm
                            </Button>
                        </>
                    )}
                </Grid>
            </Flex>
        </Box>
    );
}
Editor.propTypes = { image: PropTypes.string.isRequired, onClear: PropTypes.func.isRequired };

export default Editor;